import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Coffee, Search } from "lucide-react";
import { InstallButton } from "./InstallButton";
import { PushOptInPill } from "./PushOptInPill";
import { PollVotePill } from "./PollVotePill";
import SocialLinks from "./SocialLinks";
import { ShareButton } from "./ShareButton";
import { useTheme } from "../context/ThemeContext";
import { BUY_ME_A_COFFEE_URL } from "../lib/brand";

// Desktop-only "Buy us a coffee" pill — sits next to the Get-alerts
// pill in the sticky action bar. Mobile already has its own 🍔 CTA in
// the header brand row + drawer, so this stays hidden below `sm` to
// avoid duplicating the tip path on small screens.
const BuyCoffeeLink = ({ large = false } = {}) => (
  <a
    href={BUY_ME_A_COFFEE_URL}
    target="_blank"
    rel="noreferrer"
    data-testid="site-action-buy-coffee"
    aria-label="🍔 Buy LAST a Batch!"
    title="🍔 Buy LAST a Batch!"
    className={`inline-flex items-center justify-center gap-1.5 rounded-full border border-[#FFDD00] bg-[#FFDD00] font-display text-xs font-bold text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:border-[#0A0F17] hover:text-[#FFDD00] sm:h-auto sm:w-auto sm:px-3 sm:py-1.5 ${
      large ? "h-11 w-11" : "h-6 w-6"
    }`}
  >
    <Coffee size={large ? 20 : 16} strokeWidth={2.5} className="sm:hidden" />
    <Coffee size={14} strokeWidth={2.5} className="hidden sm:inline" />
    <span className="hidden sm:inline">🍔 Buy LAST a Batch!</span>
  </a>
);

/**
 * Site-wide action bar: Get-alerts pill + Search + Install + Share +
 * the four social icons (Instagram / Facebook / X / WhatsApp). Rendered
 * just below the sticky header on every public page so visitors always
 * have the same set of quick actions at the top of the screen
 * regardless of which page they're on. Always fully visible — no
 * scroll-away/collapse behaviour (removed per user request; the
 * header + this bar now stay fixed and full-size at all times, only
 * the ticker above scrolls away with normal page flow).
 *
 * Each item is independently hideable — PushOptInPill respects whether
 * web push is even configured, InstallButton respects the admin's
 * `install_button_visible` theme flag, and SocialLinks only renders the
 * icons whose URLs are non-empty in Admin → Theme. The Share button is
 * always rendered and uses the current URL + document title, so it
 * works on every public page (Home, News, Videos, Stats, Blast,
 * galleries, custom pages, post detail, etc.) on every screen size.
 */
export default function SiteActionBar({ inline = false, large = false, trailing = null } = {}) {
  const { theme } = useTheme();
  const location = useLocation();
  // Blog post pages get a "Back to home" link on the left of this bar
  // (the byline row above the article has its own separate one).
  const isPostPage = /^\/post\/[^/]+\/?$/.test(location.pathname);

  // Re-read the URL + document title whenever the route changes, so the
  // Share button always points at the current page. `<DocumentTitle />`
  // mutates `document.title` after route changes; we read it on a
  // micro-delay so the change has landed by the time we capture it.
  const [shareCtx, setShareCtx] = useState(() => ({
    url: typeof window !== "undefined" ? window.location.href : "",
    title: typeof document !== "undefined" ? document.title || "LAST" : "LAST",
  }));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const sync = () => setShareCtx({
      url: window.location.href,
      title: document.title || "LAST",
    });
    sync();
    // <DocumentTitle /> updates `document.title` in its own effect after
    // this one runs, so re-read on the next tick to catch the new title.
    const id = window.setTimeout(sync, 50);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.search]);

  // Inline mode: render just the icon row (no white/40 background bar,
  // no scroll-away animation) so the action bar can sit on the same line
  // as a page heading without a stand-alone strip.
  if (inline) {
    return (
      <div
        data-testid="site-action-bar-inline"
        className="flex flex-wrap items-center justify-start gap-1 sm:justify-end sm:gap-2"
      >
        <PushOptInPill alwaysShow />
        <BuyCoffeeLink />
        <PollVotePill />
        <ShareButton url={shareCtx.url} title={shareCtx.title} compact />
        <Link
          to="/archive#search"
          data-testid="site-action-search"
          aria-label="Search the site"
          title="Search the site"
          className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-black/10 bg-white text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white sm:h-10 sm:w-10"
        >
          <Search size={16} strokeWidth={2.5} />
        </Link>
        <span className="inline-flex lg:hidden">
          <InstallButton
            compact
            testId="site-action-install"
            disabled={theme.install_button_visible === false}
          />
        </span>
        <SocialLinks />
      </div>
    );
  }

  return (
    <div
      data-testid="site-action-bar"
      className="border-t border-black/5 bg-white/40 backdrop-blur"
    >
      <div
        className={`mx-auto flex max-w-7xl flex-wrap items-center gap-1 px-5 py-1.5 sm:gap-2 sm:px-8 sm:py-2 ${
          isPostPage ? "justify-between" : "justify-center sm:justify-end"
        }`}
      >
        {isPostPage ? (
          <Link
            to="/"
            data-testid="site-action-back-home"
            aria-label="Back to home"
            title="Back to home"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center gap-1.5 rounded-full border border-black/10 bg-white font-display text-xs font-bold text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white sm:h-10 sm:w-auto sm:justify-start sm:px-3"
          >
            <ArrowLeft size={14} strokeWidth={2.5} /> <span className="hidden sm:inline">Back to home</span>
          </Link>
        ) : null}
        <div
          className={`flex flex-wrap items-center gap-1 sm:gap-2 ${
            isPostPage ? "justify-end" : "justify-center sm:justify-end"
          }`}
        >
        <PushOptInPill alwaysShow large={large} />
        <BuyCoffeeLink large={large} />
        <PollVotePill large={large} />
        <ShareButton url={shareCtx.url} title={shareCtx.title} compact large={large} />
        <Link
          to="/archive#search"
          data-testid="site-action-search"
          aria-label="Search the site"
          title="Search the site"
          className={`inline-flex items-center justify-center rounded-full border border-black/10 bg-white text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-white sm:h-10 sm:w-10 ${
            large ? "h-11 w-11" : "h-6 w-6"
          }`}
        >
          <Search size={large ? 20 : 16} strokeWidth={2.5} />
        </Link>
        {/* Install is only useful on mobile / tablet (Safari Mac can't
            install web apps, Chrome desktop install is hardly used by
            our audience). Hide it from desktop layouts entirely. */}
        <span className="inline-flex lg:hidden">
          <InstallButton
            compact
            large={large}
            testId="site-action-install"
            disabled={theme.install_button_visible === false}
          />
        </span>
        <SocialLinks large={large} />
        {trailing}
        </div>
      </div>
    </div>
  );
}

